import React, { ChangeEvent } from "react";
import { Prompt } from "react-router";
import { V2 } from "../../Api";
import { RouteComponentPropsHistory } from "../../types";
import { formatPhone } from "../../Utilities";
import Checkbox from "../Checkbox";

import DeletionConfirmationDialog from "../DeletionConfirmationDialog";
import OverflowMenuItem from "../OverflowMenuItem";
import ViewToolbar from "../ViewToolbar";

import withTitle from "../withTitle";

interface IMessagingReminderRuleEditViewState {
    isLoading: boolean | string;
    isSubmitting: boolean | string;
    hasChanged: boolean;
    isDeleteDialogOpen: boolean;

    rule: any;
    numbers: any[];

    name: string;
    isRelative: boolean;
    sendTime: string;
    sendTimeMinutes: string;
    conditions: any[];
    templateContent: string;
    numberId: string;
    isDisabled: boolean;
    isFinal: boolean;
    extra: any[];
}

interface IMessagingReminderRuleEditViewProps extends RouteComponentPropsHistory<any, any> {

}

const OperatorsMap: { [num: number]: string } = {
    0: "Equals",
    1: "Not Equals",
    2: "Greater Than",
    3: "Greater Or Equal",
    4: "Less Than",
    5: "Less Or Equal",
    6: "Contains",
    7: "Starts With",
    8: "Ends With",
};

const PropertiesMap: { [prop: string]: { displayName: string, type: string } } = {
    "Group": { displayName: "Group", type: "string" },
    "AppointmentTimestamp": { displayName: "Appointment Date & Time", type: "datetime" },
    "AppointmentLocation": { displayName: "Appointment Office Location", type: "string" },
    "AppointmentProvider": { displayName: "Appointment Provider", type: "string" },
    "AppointmentType": { displayName: "Appointment Type", type: "string" },
    "AppointmentWeekday": { displayName: "Appointment Weekday", type: "number" },
    "Destination": { displayName: "Patient Phone Number", type: "string" },
    "PatientId": { displayName: "Patient Chart #", type: "string" },
};

class MessagingReminderRuleEditView extends React.Component<IMessagingReminderRuleEditViewProps, IMessagingReminderRuleEditViewState> {
    constructor(props: Readonly<IMessagingReminderRuleEditViewProps>) {
        super(props);

        const state: IMessagingReminderRuleEditViewState = {
            isLoading: !!props.match.params.id,
            isSubmitting: false,
            hasChanged: false,
            isDeleteDialogOpen: false,

            rule: {},
            numbers: [],

            name: "",
            isRelative: false,
            sendTime: "",
            sendTimeMinutes: "",
            conditions: [],
            templateContent: "",
            numberId: "",
            isDisabled: false,
            isFinal: false,
            extra: [],
        };

        this.state = state;

        this.formInputChanged = this.formInputChanged.bind(this);
        this.saveChanges = this.saveChanges.bind(this);
        this.cancelForm = this.cancelForm.bind(this);
        this.addCondition = this.addCondition.bind(this);
        this.addExtra = this.addExtra.bind(this);
        this.toggleDeleteDialog = this.toggleDeleteDialog.bind(this);
        this.deleteRule = this.deleteRule.bind(this);
    }

    public componentDidMount() {
        this.loadNumbers();
        if (this.props.match.params.id) {
            this.loadRule(this.props.match.params.id);
        }
        else if (this.props.history.location.state?.copyFromId) {
            // New rules based on existing rule
            this.loadRule(this.props.history.location.state.copyFromId, true);
        }
    }

    public render() {
        return (
            <div className="viewer-root input-form">
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <ViewToolbar>
                    <OverflowMenuItem>
                        {this.props.match.params.id ? <button type="button" className="text-with-icon" onClick={this.toggleDeleteDialog}><i className="icon-trash" /><span>Delete Rule...</span></button> : null}
                    </OverflowMenuItem>
                </ViewToolbar>
                <DeletionConfirmationDialog isOpen={this.state.isDeleteDialogOpen} description="Are you sure you want to delete this rule?" onRequestClose={this.toggleDeleteDialog} onConfirm={this.deleteRule} onSuccess={this.cancelForm} />
                <div className="column-root">
                    <section className="info-block">
                        <header>
                            <h3>Rule info</h3>
                        </header>

                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-name">Name</label>
                                <input type="text" id="frm-name" name="name" value={this.state.name} onChange={this.formInputChanged} />
                            </div>
                        </section>

                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-send-time">{this.state.isRelative ? "Send minutes before" : "Send time of day"}</label>
                                {this.state.isRelative
                                    ? <input type="number" id="frm-send-time" name="sendTimeMinutes" value={this.state.sendTimeMinutes} min={0} max={1440} step={1} onChange={this.formInputChanged} />
                                    : <input type="time" id="frm-send-time" name="sendTime" value={this.state.sendTime} onChange={this.formInputChanged} />}
                            </div>
                            <div className="input-form-group">
                                <label htmlFor="frm-is-relative">&nbsp;</label>
                                <div className="input-form-group-check-inline">
                                    <Checkbox fancy={true} id="frm-is-relative" name="isRelative" checked={this.state.isRelative} onChange={this.formInputChanged}>Relative to appointment time</Checkbox>
                                </div>
                            </div>
                        </section>
                        {this.state.isRelative
                            ? <small>
                                <br />
                                Relative time means a message's send time will be calculated from the time of day of the appointment, minus the number of minutes specified above.
                            </small>
                            : null}
                    </section>
                    <section className="info-block">
                        <header>
                            <h3>Additional Info</h3>
                        </header>

                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-number">Number to Send From</label>
                                <select id="frm-number" name="numberId" value={this.state.numberId} onChange={this.formInputChanged}>
                                    <option value="">Inherit</option>
                                    {this.state.numbers.map(a => <option key={a.id} value={a.id}>
                                        {formatPhone(a.number)} &mdash; {a.name}
                                    </option>)}
                                </select>
                            </div>
                        </section>
                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-is-disabled">Disable Rule</label>
                                <div className="input-form-group-check-inline">
                                    <Checkbox fancy={true} id="frm-is-disabled" name="isDisabled" checked={this.state.isDisabled} onChange={this.formInputChanged}>Disable rule. If checked, this rule will be skipped.</Checkbox>
                                </div>
                            </div>
                        </section>
                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-is-final">Final Rule</label>
                                <div className="input-form-group-check-inline">
                                    <Checkbox fancy={true} id="frm-is-final" name="isFinal" checked={this.state.isFinal} onChange={this.formInputChanged}>Final rule. If checked, this rule's message will be used.</Checkbox>
                                </div>
                                &nbsp;
                                <div><small>At least one rule must be final for a message to be created for a reminder.</small></div>
                            </div>
                        </section>
                    </section>
                </div>
                <p>
                    For this rule to match, <em>all</em> of the following conditions must match.
                </p>
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th>Property</th>
                            <th className="col-smallest">Operator</th>
                            <th>Value</th>
                            <th className="col-smallest">Negate</th>
                            <th className="col-smallest" />
                            <th className="col-smallest" />
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.isLoading === true
                            ? <tr><td colSpan={10}><div className="spinner lds-dual-ring" /></td></tr>
                            : typeof this.state.isLoading === "string"
                                ? <tr><td colSpan={10}><div className="error-text">{this.state.isLoading}</div></td></tr>
                                : this.state.conditions?.length === 0
                                    ? <tr><td colSpan={10}>There are no rules</td></tr>
                                    : this.state.conditions?.map((a, i) => a.isEdit
                                        ? <tr key={a.id}>
                                            <td className="col-nopad-v">
                                                <select name="property" value={a.property} onChange={e => this.conditionPropChange(a.id, e)}>
                                                    <option></option>
                                                    {Object.keys(PropertiesMap).map(key => <option key={key} value={key}>{PropertiesMap[key].displayName}</option>)}
                                                </select>
                                            </td>
                                            <td className="col-nopad-v">
                                                <select name="operator" value={a.operator} onChange={e => this.conditionPropChange(a.id, e)}>
                                                    <option></option>
                                                    {Object.keys(OperatorsMap).map(key => <option key={key} value={key}>{OperatorsMap[+key]}</option>)}
                                                </select>
                                            </td>
                                            <td className="col-nopad-v">
                                                <input type="text" name="value" value={a.value} onChange={e => this.conditionPropChange(a.id, e)} />
                                            </td>
                                            <td className="col-nopad-v">
                                                <select name="negate" value={a.negate ? "yes" : "no"} onChange={e => this.conditionPropChange(a.id, e)}>
                                                    <option value="no">No</option>
                                                    <option value="yes">Yes</option>
                                                </select>
                                            </td>
                                            <td className="col-nopad" colSpan={2}>
                                                <button type="button" className="bare icon-check" onClick={() => this.toggleEditCondition(a.id)} title="Save" disabled={a.isDeleted} />
                                            </td>
                                        </tr>
                                        : <tr key={a.id} className={(a.isDeleted ? "row-strike-text row-gray-text" : a.isNew ? "row-ltblue" : undefined)}>
                                            <td>{PropertiesMap[a.property]?.displayName || a.property}</td>
                                            <td>{OperatorsMap[a.operator]}</td>
                                            <td>{a.value}</td>
                                            <td>{a.negate ? "Yes" : "No"}</td>
                                            <td className="col-nopad">
                                                <button type="button" className="bare icon-pencil" onClick={() => this.toggleEditCondition(a.id)} title="Edit" disabled={a.isDeleted} />
                                                <button type="button" className="bare icon-chevron-up" onClick={() => this.moveCondition(a.id, "up")} title="Move up" disabled={a.isDeleted || i === 0} />
                                                <button type="button" className="bare icon-chevron-down" onClick={() => this.moveCondition(a.id, "down")} title="Move down" disabled={a.isDeleted || i === this.state.conditions?.length - 1} />
                                            </td>
                                            <td className="col-nopad">
                                                {!a.isDeleted
                                                    ? <button type="button" className="bare red icon-times" onClick={() => this.deleteCondition(a.id)} title="Delete" />
                                                    : <button type="button" className="bare icon-repeat" onClick={() => this.deleteCondition(a.id, false)} title="Undo. Re-add" />}
                                            </td>
                                        </tr>)}
                    </tbody>
                </table>
                &nbsp;
                <div className="action-panel-dialog">
                    <span className="spring" />
                    <button type="button" className="button text-with-icon" onClick={this.addCondition}><i className="icon-plus" /><span>Add Condition</span></button>
                </div>
                {
                    !this.state.isFinal
                        ? <>
                            <h3>Extra Properties</h3>
                            <div>
                                You can add extra properties for use in a later rule's message template.
                            </div>
                            &nbsp;
                            <table className="table-vertical">
                                <thead>
                                    <tr>
                                        <th>Property</th>
                                        <th>Value</th>
                                        <th className="col-smallest" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.state.isLoading === true
                                        ? <tr><td colSpan={10}><div className="spinner lds-dual-ring" /></td></tr>
                                        : typeof this.state.isLoading === "string"
                                            ? <tr><td colSpan={10}><div className="error-text">Failed to load</div></td></tr>
                                            : this.state.extra?.length === 0
                                                ? <tr><td colSpan={10}>There are no extra properties</td></tr>
                                                : this.state.extra?.map((a, i) => a.isEdit
                                                    ? <tr key={a.id}>
                                                        <td className="col-nopad-v">
                                                            <input type="text" name="property" value={a.property} onChange={e => this.extraPropChange(a.id, e)} />
                                                        </td>
                                                        <td className="col-nopad-v">
                                                            <input type="text" name="value" value={a.value} onChange={e => this.extraPropChange(a.id, e)} />
                                                        </td>
                                                        <td className="col-nopad" colSpan={2}>
                                                            <button type="button" className="bare icon-check" onClick={() => this.toggleEditExtra(a.id)} title="Save" disabled={a.isDeleted} />
                                                        </td>
                                                    </tr>
                                                    : <tr key={a.id} className={(a.isDeleted ? "row-strike-text row-gray-text" : a.isNew ? "row-ltblue" : undefined)}>
                                                        <td>{a.property}</td>
                                                        <td>{a.value}</td>
                                                        <td className="col-nopad">
                                                            <button type="button" className="bare icon-pencil" onClick={() => this.toggleEditExtra(a.id)} title="Edit" disabled={a.isDeleted} />
                                                            {!a.isDeleted
                                                                ? <button type="button" className="bare red icon-times" onClick={() => this.deleteExtra(a.id)} title="Delete" />
                                                                : <button type="button" className="bare icon-repeat" onClick={() => this.deleteExtra(a.id, false)} title="Undo. Re-add" />}
                                                        </td>
                                                    </tr>)}
                                </tbody>
                            </table>
                            &nbsp;
                            <div className="action-panel-dialog">
                                <span className="spring" />
                                <button type="button" className="button text-with-icon" onClick={this.addExtra}><i className="icon-plus" /><span>Add Extra Property</span></button>
                            </div>
                        </>
                        : <>
                            <h3>Message Template</h3>
                            <div>
                                <textarea className="input-textarea-medium" name="templateContent" value={this.state.templateContent} onChange={this.formInputChanged} />
                            </div>
                        </>
                }
                &nbsp;
                {this.state.isSubmitting === true
                    ? <div className="spinner lds-dual-ring" />
                    : typeof this.state.isSubmitting === "string"
                        ? <div className="error-text">{this.state.isSubmitting}</div>
                        : null}
                <div className="action-panel">
                    <button type="button" className="button text-with-icon push-right" onClick={this.saveChanges}><i className="icon-check" /><span>Save</span></button>
                    <button type="button" className="button text-with-icon" onClick={this.cancelForm}><i className="icon-times" /><span>Cancel</span></button>
                </div>
            </div>
        );
    }

    private formInputChanged(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) {
        const value = (e.currentTarget.type === "checkbox") ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            hasChanged: true,
            [name]: value
        } as any);
    }

    private conditionPropChange(id: string, e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) {
        const conditions = this.state.conditions;
        const row = conditions.find(a => a.id === id);
        if (!row) {
            return;
        }

        const name = e.currentTarget.name;
        let value: any = e.currentTarget.value;

        switch (name) {
            case "negate":
                value = value === "yes";
                break;
            case "operator":
                value = +value;
                break;
        }

        row[name] = value;

        this.setState({
            hasChanged: true,
            conditions
        });
    }

    private extraPropChange(id: string, e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) {
        const extra = this.state.extra;
        const row = extra.find(a => a.id === id);
        if (!row) {
            return;
        }

        const name = e.currentTarget.name;
        const value = e.currentTarget.value;

        row[name] = value;

        this.setState({
            hasChanged: true,
            extra
        });
    }

    private async loadNumbers() {
        try {
            const result = await V2.get<any>(`messaging/numbers/summary`);

            this.setState({
                numbers: result.numbers
            });
        }
        catch (e) {
            // TODO: Where should we show this error?
        }
    }

    private async loadRule(id: string, isCopy = false) {
        try {
            const result = await V2.get<any>(`messaging/reminders/rules/${id}`);

            // Copy list of conditions and extras
            const conditions = result.conditions.map((a: any) => ({ ...a }));
            const extra = result.extra?.map((a: any) => ({ ...a }));

            let sendTimeMinutes = result.sendTime ? "0" : "";
            if (result.isRelative !== null && result.isRelative) {
                const parts = result.sendTime.match(/(\d\d):(\d\d):\d\d/);
                sendTimeMinutes = ((+parts[1] * 60) + (+parts[2])).toString();
            }

            this.setState({
                isLoading: false,
                rule: isCopy ? {} : result,

                name: isCopy ? `${result.name} (copy)` : result.name,
                sendTime: result.sendTime || "",
                sendTimeMinutes,
                isRelative: result.isRelative || false,
                templateContent: result.template?.content || "",
                conditions,
                numberId: result.number?.id || "",
                isDisabled: result.isDisabled,
                isFinal: result.isFinal,
                extra
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load rules",
            });
        }
    }

    private async saveChanges() {
        try {
            this.setState({
                isSubmitting: true
            });

            if (!this.state.name.trim()) {
                this.setState({
                    isSubmitting: "Name must not be blank"
                });
                return;
            }
            else if (this.state.isFinal && !this.state.templateContent.trim()) {
                this.setState({
                    isSubmitting: "Message template must not be blank"
                });
                return;
            }

            const data: any = {};

            if (this.state.name !== this.state.rule.name) {
                data.name = this.state.name;
            }
            if (this.state.templateContent !== this.state.rule.template?.content) {
                data.templateContent = this.state.templateContent;
            }
            if (this.state.isRelative !== this.state.rule.isRelative) {
                data.isRelative = this.state.isRelative;
            }
            if (this.state.isDisabled !== this.state.rule.isDisabled) {
                data.isDisabled = this.state.isDisabled;
            }
            if (this.state.isFinal !== this.state.rule.isFinal) {
                data.isFinal = this.state.isFinal;
            }
            if (this.state.numberId !== this.state.rule.numberId) {
                data.numberId = this.state.numberId;
            }
            let sendTime = "";
            if (this.state.isFinal) {
                sendTime = this.state.sendTime;
                if (this.state.isRelative) {
                    // Must convert minutes to hours:minutes:seconds
                    if (!this.state.sendTimeMinutes) {
                        this.setState({
                            isLoading: "A time must be set"
                        });
                        return;
                    }

                    const minutes = +this.state.sendTimeMinutes;
                    sendTime = `${Math.floor(minutes / 60)}:${Math.floor(minutes % 60)}:00`;
                }
                else {
                    if (sendTime.length < 8) {
                        sendTime = sendTime + ":00";
                    }
                }
            }

            if (sendTime !== this.state.rule.sendTime) {
                data.sendTime = sendTime;
            }

            const conditions = this.state.conditions.filter(a => !a.isDeleted);
            const extra = !this.state.isFinal ? this.state.extra.filter(a => !a.isDeleted) : [];

            // Adjust ordering to match displayed order
            let order = 1;
            for (const item of conditions) {
                item.order = order;
                order++;
            }
            data.conditions = conditions;
            data.extra = extra;

            if (this.props.match.params.id) {
                await V2.patch<void>(`messaging/reminders/rules/${this.props.match.params.id}`, data);
            }
            else {
                await V2.post<void>("messaging/reminders/rules", data);
            }

            this.setState({
                hasChanged: false,
                isSubmitting: false
            }, () => this.cancelForm());
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message || "Could not submit"
            });
        }
    }

    private deleteRule() {
        return V2.delete<void>(`messaging/reminders/rules/${this.props.match.params.id}`);
    }

    private cancelForm() {
        this.props.history.goBack();
    }

    private addCondition() {
        const conditions = this.state.conditions;

        conditions.push({
            id: Date.now().toString(),
            isEdit: true
        });

        this.setState({
            hasChanged: true,
            conditions
        });
    }

    private deleteCondition(id: string, value = true) {
        const conditions = this.state.conditions;
        const row = conditions.find(a => a.id === id);
        if (!row) {
            return;
        }

        row.isDeleted = value;

        this.setState({
            hasChanged: true,
            conditions
        });
    }

    private moveCondition(id: string, direction: "up" | "down") {
        const conditions = this.state.conditions;
        let rowIndex = conditions.findIndex(a => a.id === id);
        if (rowIndex === -1) {
            return;
        }

        if ((direction === "up" && rowIndex === 0) || (direction === "down" && rowIndex === conditions.length - 1)) {
            return;
        }

        const [row] = conditions.splice(rowIndex, 1);

        rowIndex = direction === "up"
            ? rowIndex - 1
            : rowIndex + 1;

        conditions.splice(rowIndex, 0, row);

        this.setState({
            hasChanged: true,
            conditions
        });
    }

    private toggleEditCondition(id: string) {
        const conditions = this.state.conditions;
        const row = conditions.find(a => a.id === id);
        if (!row) {
            return;
        }

        row.isEdit = !row.isEdit;

        this.setState({
            conditions
        });
    }

    private addExtra() {
        const extra = this.state.extra;

        extra.push({
            id: Date.now().toString(),
            isEdit: true
        });

        this.setState({
            hasChanged: true,
            extra
        });
    }

    private deleteExtra(id: string, value = true) {
        const extra = this.state.extra;
        const row = extra.find(a => a.id === id);
        if (!row) {
            return;
        }

        row.isDeleted = value;

        this.setState({
            hasChanged: true,
            extra
        });
    }

    private moveExtra(id: string, direction: "up" | "down") {
        const extra = this.state.extra;
        let rowIndex = extra.findIndex(a => a.id === id);
        if (rowIndex === -1) {
            return;
        }

        if ((direction === "up" && rowIndex === 0) || (direction === "down" && rowIndex === extra.length - 1)) {
            return;
        }

        const [row] = extra.splice(rowIndex, 1);

        rowIndex = direction === "up"
            ? rowIndex - 1
            : rowIndex + 1;

        extra.splice(rowIndex, 0, row);

        this.setState({
            hasChanged: true,
            extra
        });
    }

    private toggleEditExtra(id: string) {
        const extra = this.state.extra;
        const row = extra.find(a => a.id === id);
        if (!row) {
            return;
        }

        row.isEdit = !row.isEdit;

        this.setState({
            extra
        });
    }

    private toggleDeleteDialog() {
        const isDeleteDialogOpen = this.state.isDeleteDialogOpen;

        this.setState({
            isDeleteDialogOpen: !isDeleteDialogOpen
        });
    }
}

export default withTitle("Add/Edit Rule", MessagingReminderRuleEditView);
